import React from "react";
import { Navigate, RouteObject } from "react-router-dom";

import { ADMIN_ROUTES, AI_ROUTES } from "../routes";

export const AiPageRoutes: RouteObject[] = [
  {
    path: AI_ROUTES.Ai,
    element: <Navigate to={AI_ROUTES.AiWorkflowBuilder} replace />,
  },
  {
    path: `${AI_ROUTES.AiWorkflowBuilder}/:workflowId?`,
    lazy: async () => {
      const { AdminAiRoute } =
        await import("@src/features/ai/pages/ai/AdminAiRoute");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiWorkflowBuilder}
            isEnabled={({ isAdmin }) => isAdmin}
            children={AdminAiRoute}
            componentProps={{
              sx: { width: "100%", pl: 2, pr: 2, pt: 2, mt: 0 },
            }}
          />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiAgents,
    lazy: async () => {
      const { AgentsRoute } =
        await import("@src/features/ai/pages/ai/AgentsRoute");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiAgents}
            isEnabled={({ isAdmin }) => isAdmin}
            children={AgentsRoute}
          />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiAgentDetail,
    lazy: async () => {
      const { AgentPromptsRoute } =
        await import("@src/features/ai/pages/ai/AgentPromptsRoute");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiAgentDetail}
            isEnabled={({ isAdmin }) => isAdmin}
            children={AgentPromptsRoute}
          />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiAgenticLogic,
    lazy: async () => {
      const { AgenticLogicRulesRoute } =
        await import("@src/features/ai/pages/agentic-logic/routes/AgenticLogicRules.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiAgenticLogic}
            isEnabled={({ isAdmin }) => isAdmin}
            children={AgenticLogicRulesRoute}
          />
        ),
      };
    },
  },
  // `…/new` and `…/:ruleId` are views of the route above now, selected by the
  // `rule` query param; these two remain only to redirect links minted before
  // the change.
  {
    path: AI_ROUTES.AiAgenticLogicNewRule,
    lazy: async () => {
      const { AgenticLogicNewRuleRedirect } =
        await import("@src/features/ai/pages/agentic-logic/routes/AgenticLogicLegacyRedirect");
      const { ADMIN_AGENTIC_LOGIC_SURFACE } =
        await import("@src/features/ai/pages/agentic-logic/surface/surface");
      return {
        element: (
          <AgenticLogicNewRuleRedirect surface={ADMIN_AGENTIC_LOGIC_SURFACE} />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiAgenticLogicRule,
    lazy: async () => {
      const { AgenticLogicRuleRedirect } =
        await import("@src/features/ai/pages/agentic-logic/routes/AgenticLogicLegacyRedirect");
      const { ADMIN_AGENTIC_LOGIC_SURFACE } =
        await import("@src/features/ai/pages/agentic-logic/surface/surface");
      return {
        element: (
          <AgenticLogicRuleRedirect surface={ADMIN_AGENTIC_LOGIC_SURFACE} />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiTestCategorizations,
    lazy: async () => {
      const { AiTestCategorizationsRoute } =
        await import("@src/features/ai/pages/ai/AiTestCategorizationsRoute");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiTestCategorizations}
            isEnabled={({ isAdmin }) => isAdmin}
            children={AiTestCategorizationsRoute}
          />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiBotReview,
    lazy: async () => {
      const { QaReviewRoute } =
        await import("@src/features/ai/pages/admin/qa-review/QaReview.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiBotReview}
            adminOnly={true}
            isEnabled={() => true}
            children={QaReviewRoute}
          />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiExperimentClassifications,
    lazy: async () => {
      const { ExperimentClassificationsRoute } =
        await import("@src/features/ai/pages/admin/experiment-classifications/ExperimentClassifications.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiExperimentClassifications}
            adminOnly={true}
            isEnabled={() => true}
            children={ExperimentClassificationsRoute}
          />
        ),
      };
    },
  },
  {
    path: AI_ROUTES.AiExperimentClassificationDetail,
    lazy: async () => {
      const { ExperimentClassificationDetailRoute } =
        await import("@src/features/ai/pages/admin/experiment-classifications/ExperimentClassificationDetail.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={AI_ROUTES.AiExperimentClassificationDetail}
            adminOnly={true}
            isEnabled={() => true}
            children={ExperimentClassificationDetailRoute}
          />
        ),
      };
    },
  },
  {
    path: ADMIN_ROUTES.QaBotReview,
    lazy: async () => {
      const { QaBotReviewRoute } =
        await import("@src/features/ai/pages/admin/qa-bot-review/QaBotReview.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={ADMIN_ROUTES.QaBotReview}
            adminOnly={true}
            isEnabled={() => true}
            children={QaBotReviewRoute}
          />
        ),
      };
    },
  },
  {
    path: ADMIN_ROUTES.PatchReview,
    lazy: async () => {
      const { PatchReviewRoute } =
        await import("@src/features/ai/pages/admin/patch-review/PatchReview.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={ADMIN_ROUTES.PatchReview}
            adminOnly={true}
            isEnabled={() => true}
            children={PatchReviewRoute}
          />
        ),
      };
    },
  },
  {
    path: ADMIN_ROUTES.AiPatchReview,
    lazy: async () => {
      const { PatchReviewRoute } =
        await import("@src/features/ai/pages/admin/patch-review/PatchReview.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={ADMIN_ROUTES.AiPatchReview}
            adminOnly={true}
            isEnabled={() => true}
            children={PatchReviewRoute}
          />
        ),
      };
    },
  },
  {
    path: ADMIN_ROUTES.PylonTicketAnalysis,
    lazy: async () => {
      const { PylonTicketAnalysisRoute } =
        await import("@src/features/ai/pages/reports/pylon/PylonTicketAnalysis.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={ADMIN_ROUTES.PylonTicketAnalysis}
            adminOnly={true}
            isEnabled={() => true}
            children={PylonTicketAnalysisRoute}
          />
        ),
      };
    },
  },
  {
    path: ADMIN_ROUTES.AiPylonTicketAnalysis,
    lazy: async () => {
      const { PylonTicketAnalysisRoute } =
        await import("@src/features/ai/pages/reports/pylon/PylonTicketAnalysis.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={ADMIN_ROUTES.AiPylonTicketAnalysis}
            adminOnly={true}
            isEnabled={() => true}
            children={PylonTicketAnalysisRoute}
          />
        ),
      };
    },
  },
  {
    path: ADMIN_ROUTES.ShiftAnalysis,
    lazy: async () => {
      const { ShiftAnalysisRoute } =
        await import("@src/features/ai/pages/reports/shift/ShiftAnalysis.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={ADMIN_ROUTES.ShiftAnalysis}
            adminOnly={true}
            isEnabled={() => true}
            children={ShiftAnalysisRoute}
          />
        ),
      };
    },
  },
  {
    path: ADMIN_ROUTES.AiShiftAnalysis,
    lazy: async () => {
      const { ShiftAnalysisRoute } =
        await import("@src/features/ai/pages/reports/shift/ShiftAnalysis.route");
      const { ScopedRoute } =
        await import("@src/components/LimitAccess/ScopedRoute");
      return {
        element: (
          <ScopedRoute
            debugId={ADMIN_ROUTES.AiShiftAnalysis}
            adminOnly={true}
            isEnabled={() => true}
            children={ShiftAnalysisRoute}
          />
        ),
      };
    },
  },
];
